import { t } from 'i18next';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { AuthorizationError } from '../../../common/errors/errors';
import { WithRouterProps, withRouter } from '../../../common/higher-order-components/with-router';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { PATHS } from '../../../routing/routes';
import { FuzzerProfile } from '../models/FuzzerProfile';
import { TracerProfile } from '../models/TracerProfile';
import { OrganizationProfilesService } from '../services/OrganizationProfilesService';
import { FuzzerController } from './FuzzerController';
import { TracerController } from './TracerController';
type Props = WithRouterProps & {};

type State = BaseState & {};

class Controller extends BaseController<Props, State> {
  private readonly profilesService = new OrganizationProfilesService();

  private profileId: number;
  private fromProfile: TracerProfile | FuzzerProfile | null;

  constructor(props: Props, context: unknown) {
    super(props, context);

    ({ profileId: this.profileId } = this.parseNumericIds(['profileId']));
    this.fromProfile = null;
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadProfile();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.profilesService.cancelAllRequests();
  }

  protected shouldCheckPermissionToAccess(): boolean {
    return true;
  }

  protected async checkPermissionToAccess(): Promise<void> {
    const canCreateProfile = await this.permissionsService.canCreateProfile();
    if (!canCreateProfile) {
      throw new AuthorizationError(PATHS.PROFILES);
    }
  }

  render() {
    const breadcrumbPath = this.getCreatePath();
    if (!this.fromProfile) {
      return this.renderBase(<SecondaryNav title={t('duplicateProfile')} getBreadcrumbs={this.getBreadcrumbs} loading={true} />);
    }
    return this.renderBase(
      <>
        {this.fromProfile instanceof TracerProfile && (
          <TracerController
            profile={this.fromProfile}
            title={t('duplicateProfile')}
            breadcrumbName={t('duplicateProfile')}
            breadcrumbPath={breadcrumbPath}
            editable={true}
          />
        )}
        {this.fromProfile instanceof FuzzerProfile && (
          <FuzzerController
            profile={this.fromProfile}
            isEditable={true}
            breadcrumbName={t('duplicateProfile')}
            breadcrumbPath={breadcrumbPath}
            title={t('duplicateProfile')}
          />
        )}
      </>
    );
  }

  private async loadProfile() {
    try {
      this.fromProfile = await this.profilesService.getProfile(this.profileId);
      this.forceUpdate();
    } catch (e: unknown) {
      this.handleError(e, { invalidUrl: t('nonExistingProfile') });
    }
  }

  private getCreatePath() {
    return UrlHelper.generatePath(PATHS.PROFILES_CREATE_TEMPLATE, { profileId: this.profileId.toString() });
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getProfileBreadcrumb({ label: t('duplicateProfile'), path: this.getCreatePath() });
  }
}

export const DuplicateProfileController = withRouter(Controller);
